import React, { useCallback, useEffect, useRef, useState } from 'react';
import './LegalBoardTab.css';
import { can } from '../../../utils/permissions';
import { fetchLegalBoardOverview, createLegalBoardTerm, cancelLegalBoardTerm } from './api';
import { OFFICIAL_LINKS } from './constants';
import { STEP_KEYS, getWizardStepIndex, newRequestId, stripeDeadline, stripeStateUnknown } from './legalBoardUi';
import { ExternalLink } from './LegalBoardPrimitives';
import LegalBoardSteps from './LegalBoardSteps';
import LegalBoardWizard from './LegalBoardWizard';
import LegalBoardCorrection from './LegalBoardCorrection';

const SITUATION_OPTIONS = [
    {
        key: 'correct',
        title: 'La junta que consta al Registre és correcta',
        description: 'La registrem a Aleta tal com està inscrita, sense presentar cap canvi.',
        changeType: 'initial',
        alreadyRegistered: true,
    },
    {
        key: 'total',
        title: 'Hem canviat tota la junta',
        description: 'Ha acabat el mandat i s\'ha escollit una junta nova.',
        changeType: 'total',
    },
    {
        key: 'partial',
        title: 'Només han canviat alguns càrrecs',
        description: 'El mandat continua però canvien persones o càrrecs.',
        changeType: 'partial',
    },
    {
        key: 'reelection',
        title: 'Hem reelegit les mateixes persones',
        description: 'Nou mandat amb la mateixa composició.',
        changeType: 'reelection',
        requiresCurrent: true,
    },
];

function LegalBoardTab({ userInfo, userId, castellersInfo, stripeReturn = false, onStripeReturnHandled, onBadgeChange, onChangeTab }) {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [view, setView] = useState('steps'); // 'steps' | 'situation' | 'unknown' | 'wizard' | 'correction'
    const [unknownBack, setUnknownBack] = useState('steps');
    const [wizardTermId, setWizardTermId] = useState(null);
    const [wizardStep, setWizardStep] = useState(0);
    const [openRequest, setOpenRequest] = useState(null); // { key, id } → LegalBoardSteps opens that step
    const [busy, setBusy] = useState(false);
    const stripeReturnHandled = useRef(false);

    const hasAccess = can(userInfo, 'ACCESS_QUOTES');
    const canManage = overview ? Boolean(overview.canManage) : can(userInfo, 'MANAGE_LEGAL_BOARD');

    const applyOverview = useCallback((payload) => {
        setOverview(payload);
        if (typeof onBadgeChange === 'function') onBadgeChange(payload?.badge || null);
    }, [onBadgeChange]);

    const loadOverview = useCallback(async ({ refresh = false, silent = false } = {}) => {
        if (!userId || !hasAccess) {
            setLoading(false);
            return null;
        }
        if (!silent) setLoading(true);
        setError(null);
        try {
            const payload = await fetchLegalBoardOverview(userId, { refresh });
            applyOverview(payload);
            return payload;
        } catch (loadError) {
            setError(loadError.message || 'No s\'ha pogut carregar la junta legal.');
            return null;
        } finally {
            setLoading(false);
        }
    }, [userId, hasAccess, applyOverview]);

    const requestOpen = (key) => setOpenRequest({ key, id: `${key}-${Date.now()}-${Math.random()}` });

    useEffect(() => {
        const returning = stripeReturn && !stripeReturnHandled.current;
        if (returning) stripeReturnHandled.current = true;
        loadOverview({ refresh: returning }).then((payload) => {
            if (!payload || !returning) return;
            const deadline = payload.stripe?.restrictedSoon ? stripeDeadline(payload.stripe, payload.today) : null;
            let text = 'Has tornat de Stripe. Hem tornat a consultar l\'estat real: revisa si encara queda alguna cosa pendent.';
            if (stripeStateUnknown(payload.stripe)) text = 'Has tornat de Stripe, però no s’ha pogut comprovar tot l’estat. Torna-ho a consultar.';
            else if (payload.stripe?.status === 'verified') text = 'Has tornat de Stripe i Stripe confirma que el compte està verificat.';
            else if (payload.stripe?.status === 'submitted') text = 'Has tornat de Stripe. Stripe està revisant les dades enviades; torna-ho a consultar més tard.';
            else if (deadline) text = `Has tornat de Stripe, però encara demana completar la verificació abans del ${deadline.short}. Revisa els passos pendents.`;
            setNotice({
                tone: stripeStateUnknown(payload.stripe) || deadline ? 'warning' : payload.stripe?.status === 'verified' ? 'success' : 'info',
                text,
            });
            requestOpen(STEP_KEYS.STRIPE);
            if (typeof onStripeReturnHandled === 'function') onStripeReturnHandled();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, hasAccess]);

    const openWizardFor = (term, stepIndex) => {
        if (!term) return;
        setWizardTermId(term.id);
        setWizardStep(typeof stepIndex === 'number' ? stepIndex : getWizardStepIndex(term));
        setView('wizard');
        setNotice(null);
    };

    const startDraft = async ({ changeType, cloneFromCurrent = false, alreadyRegistered = false, members, compositionException }) => {
        if (!canManage || busy) return;
        setBusy(true);
        setError(null);
        try {
            const response = await createLegalBoardTerm({ userId, changeType, cloneFromCurrent, alreadyRegistered, members, compositionException, requestId: newRequestId() });
            const payload = await loadOverview({ silent: true });
            const term = payload?.draft || response?.term;
            if (term) openWizardFor(term, changeType === 'initial' ? 1 : 0);
        } catch (createError) {
            setError(createError.message || 'No s\'ha pogut començar el canvi de junta.');
        } finally {
            setBusy(false);
        }
    };

    const handleSituation = (option) => {
        startDraft({
            changeType: option.changeType,
            alreadyRegistered: Boolean(option.alreadyRegistered),
            cloneFromCurrent: Boolean(overview?.current),
        });
    };

    const handleDiscardDraft = async () => {
        if (!overview?.draft || !canManage || busy) return;
        if (!window.confirm('Vols descartar aquest esborrany? Quedarà a l\'historial com a cancel·lat i no afectarà la junta vigent.')) return;
        setBusy(true);
        try {
            await cancelLegalBoardTerm({ userId, termId: overview.draft.id, requestId: newRequestId() });
            await loadOverview({ silent: true });
            setView('steps');
            setNotice({ tone: 'info', text: 'Esborrany descartat. La junta vigent no ha canviat.' });
        } catch (cancelError) {
            setError(cancelError.message || 'No s\'ha pogut descartar l\'esborrany.');
        } finally {
            setBusy(false);
        }
    };

    const handleAnnulCurrent = async () => {
        if (!overview?.current || !canManage || busy) return;
        if (!window.confirm('Vols anul·lar aquest mandat? Quedarà a l\'historial com a cancel·lat. Si n\'hi havia un d\'anterior, tornarà a ser el vigent.')) return;
        setBusy(true);
        try {
            await cancelLegalBoardTerm({ userId, termId: overview.current.id, requestId: newRequestId() });
            await loadOverview({ silent: true });
            setView('steps');
            setNotice({ tone: 'info', text: 'Mandat anul·lat. Si n\'hi havia un d\'anterior, s\'ha restaurat com a vigent.' });
        } catch (annulError) {
            setError(annulError.message || 'No s\'ha pogut anul·lar el mandat.');
        } finally {
            setBusy(false);
        }
    };

    const handleWizardClose = async ({ message, openStep } = {}) => {
        await loadOverview({ silent: true });
        setView('steps');
        if (message) setNotice(message);
        if (openStep) requestOpen(openStep);
    };

    const handleCorrectionClose = ({ message } = {}) => {
        setView('steps');
        if (message) setNotice(message);
        requestOpen(STEP_KEYS.BOARD);
    };

    const goToUnknown = (from) => {
        setUnknownBack(from);
        setView('unknown');
    };

    if (!hasAccess) {
        return (
            <div className="legal-board-tab">
                <div className="legal-board-card legal-board-empty">
                    <h2>Registre d&apos;Associacions</h2>
                    <p>No tens permís per consultar la junta legal de la colla.</p>
                </div>
            </div>
        );
    }

    if (loading && !overview) {
        return (
            <div className="legal-board-tab">
                <div className="legal-board-card legal-board-empty" role="status" aria-live="polite">
                    <div className="legal-board-spinner"></div>
                    <p>Carregant la junta legal...</p>
                </div>
            </div>
        );
    }

    if (error && !overview) {
        return (
            <div className="legal-board-tab">
                <div className="legal-board-card legal-board-empty">
                    <h2>Registre d&apos;Associacions</h2>
                    <p>{error}</p>
                    <button type="button" className="legal-board-button primary" onClick={() => loadOverview()}>Reintentar</button>
                </div>
            </div>
        );
    }

    const wizardTerm = wizardTermId
        ? [overview?.draft, overview?.current, ...(overview?.history || [])].find((term) => term && term.id === wizardTermId)
        : null;

    return (
        <div className="legal-board-tab">
            {!canManage ? (
                <p className="legal-board-readonly" role="note">
                    <i className="fas fa-eye" aria-hidden="true"></i> Només lectura: només un cap de junta pot fer canvis.
                </p>
            ) : null}

            {notice ? (
                <div className={`legal-board-alert ${notice.tone || 'info'}`} role="status">
                    <span>{notice.text}</span>
                    <button type="button" className="legal-board-alert-close" onClick={() => setNotice(null)} aria-label="Tancar l'avís">×</button>
                </div>
            ) : null}
            {error ? <div className="legal-board-alert error" role="alert">{error}</div> : null}

            {view === 'wizard' && wizardTerm ? (
                <LegalBoardWizard
                    key={wizardTerm.id}
                    term={wizardTerm}
                    overview={overview}
                    initialStep={wizardStep}
                    userId={userId}
                    canManage={canManage}
                    castellersInfo={castellersInfo}
                    onReload={() => loadOverview({ silent: true })}
                    onClose={handleWizardClose}
                    onDiscard={handleDiscardDraft}
                />
            ) : null}

            {view === 'correction' && overview?.current ? (
                <LegalBoardCorrection
                    key={overview.current.id}
                    term={overview.current}
                    overview={overview}
                    userId={userId}
                    castellersInfo={castellersInfo}
                    onReload={() => loadOverview({ silent: true })}
                    onClose={handleCorrectionClose}
                    onAnnul={handleAnnulCurrent}
                    onPrepareChange={({ members, compositionException }) => startDraft({ changeType: 'partial', cloneFromCurrent: true, members, compositionException })}
                />
            ) : null}

            {view === 'situation' ? (
                <section className="legal-board-card" aria-labelledby="legal-board-situation-title">
                    <div>
                        <h3 id="legal-board-situation-title">Quina situació té la colla?</h3>
                        <p className="legal-board-muted legal-board-small legal-board-situation-hint">
                            Pots veure quina junta consta al Registre a la <ExternalLink link={OFFICIAL_LINKS.gencatEntitiesGuide}>Guia d&apos;entitats</ExternalLink> de la Generalitat.
                            {' '}Si necessites un document oficial, <button type="button" className="legal-board-inline-action" onClick={() => goToUnknown('situation')} disabled={busy}>mira com demanar-lo</button>.
                        </p>
                    </div>
                    <div className="legal-board-options">
                        {SITUATION_OPTIONS
                            .filter((option) => !(overview?.current && option.key === 'correct'))
                            .filter((option) => !(option.requiresCurrent && !overview?.current))
                            .map((option) => (
                                <button
                                    key={option.key}
                                    type="button"
                                    className="legal-board-option"
                                    onClick={() => handleSituation(option)}
                                    disabled={busy || !canManage}
                                >
                                    <span className="legal-board-option-title">{option.title}</span>
                                    <span className="legal-board-option-description">{option.description}</span>
                                </button>
                            ))}
                    </div>
                    <div className="legal-board-actions">
                        <button type="button" className="legal-board-button link" onClick={() => setView('steps')} disabled={busy}>Tornar</button>
                    </div>
                </section>
            ) : null}

            {view === 'unknown' ? (
                <section className="legal-board-card" aria-labelledby="legal-board-unknown-title">
                    <h3 id="legal-board-unknown-title">Com saber quina junta consta al Registre</h3>
                    <ol className="legal-board-list legal-board-procedure">
                        <li>Consulta la <ExternalLink link={OFFICIAL_LINKS.gencatEntitiesGuide}>Guia d&apos;entitats</ExternalLink> de la Generalitat (cerca la colla pel nom o pel NIF). Les dades públiques poden anar amb retard.</li>
                        <li>Si necessites un document oficial, demana la <ExternalLink link={OFFICIAL_LINKS.gencatRegistryCertificate}>certificació de dades registrals</ExternalLink> (tràmit telemàtic amb certificat digital i taxa vigent). Tria la modalitat «Inscripció i òrgan de govern».</li>
                        <li>Compara-ho amb l&apos;última acta d&apos;assemblea. Si no coincideix, caldrà comunicar el canvi.</li>
                    </ol>
                    <div className="legal-board-actions">
                        <ExternalLink link={OFFICIAL_LINKS.gencatRegistryCertificate} className="legal-board-button primary">
                            Demanar la certificació al Registre
                        </ExternalLink>
                        <button type="button" className="legal-board-button link" onClick={() => setView(unknownBack)}>Tornar</button>
                    </div>
                </section>
            ) : null}

            {view === 'steps' ? (
                <LegalBoardSteps
                    overview={overview}
                    userId={userId}
                    canManage={canManage}
                    busy={busy}
                    openRequest={openRequest}
                    onReload={() => loadOverview({ silent: true })}
                    onChangeTab={onChangeTab}
                    onNotice={setNotice}
                    onConfigure={() => setView('situation')}
                    onContinueDraft={() => openWizardFor(overview?.draft)}
                    onDiscardDraft={handleDiscardDraft}
                    onCorrect={() => { setNotice(null); setView('correction'); }}
                    onPrepareChange={() => setView('situation')}
                    onHowToKnow={() => goToUnknown('steps')}
                />
            ) : null}
        </div>
    );
}

export default LegalBoardTab;
